import { Button, ButtonGroup, Chip } from '@mui/material';
import { ColDef, GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { CounterpartyModel } from '../../../../Generated/Commodity-Management-Api';
import { useLoadingState } from '../../../../Hooks';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXCheckbox, StoneXModal, StoneXTextField } from '../../../StoneXMui';
import { useSnackbar } from 'notistack';
import { RootState, useApplicationDispatch, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { addCounterparty as addCounterpartyRedux, updateCounterparty as updateCounterpartyRedux, removeCounterparty as removeCounterpartyRedux } from '../../../../Redux/Slices/CommodityManagementSlice';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';

interface CounterpartySettingsProps {
  counterparties: CounterpartyModel[];
}

export default function CounterpartySettings(props: CounterpartySettingsProps) {

  const dispatch = useApplicationDispatch();
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const { enqueueSnackbar } = useSnackbar();
  const [counterparties, setCounterparties] = useState<CounterpartyModel[]>(props.counterparties);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalCounterparty, setModalCounterparty] = useState<CounterpartyModel | undefined>();
  const saveCounterpartyLoadingState = useLoadingState();
  const gridRef = useRef<AgGridReact<CounterpartyModel>>(null);

  const columnDefs: ColDef<any>[] = [
    {
      headerName: 'Counterparty Name',
      field: 'counterpartyName',
      sortable: true,
      flex: 1,
    },
    {
      headerName: 'Credit Limit',
      field: 'creditLimit',
      sortable: true,
      flex: 1,
      filter: 'agNumberColumnFilter',
    },
    {
      headerName: 'Netting',
      field: 'netting',
      sortable: true,
      width: 105,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: true,
      valueGetter: (params: any) => !!params.data.netting,
      valueSetter: (params: any) => {
        saveField(params.data, 'netting', params.newValue);
        return true;
      },
    },
    {
      headerName: 'Set-Off',
      field: 'setoff',
      sortable: true,
      width: 105,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      editable: true,
      valueGetter: (params: any) => !!params.data.setoff,
      valueSetter: (params: any) => {
        saveField(params.data, 'setoff', params.newValue);
        return true;
      },
    },
    {
      headerName: 'Actions',
      width: userAccess.isAdmin ? 240 : 150,
      cellRenderer: (params: any) => {
        return (
          <ButtonGroup variant="text">
            <Button onClick={() => updateCounterparty(params.data)}>Edit</Button>
            <Button onClick={() => toggleCounterpartyActive(params.data)}>{params.data.active ? "Disable" : "Activate"}</Button>
            {userAccess.isAdmin && <Button onClick={() => deleteCounterparty(params.data)}>Delete</Button>}
          </ButtonGroup>
        );
      },
    },
  ];

  function saveField(counterparty: CounterpartyModel, field: keyof CounterpartyModel, value: any) {
    const updated = { ...counterparty, [field]: value };
    // Optimistic update immediately so rapid changes don't race
    setCounterparties(prev => prev.map(c => c.counterpartyId === updated.counterpartyId ? updated : c));
    CommodityManagementApi.updateCounterparty(
      null,
      updated.counterpartyId!.toString(),
      updated.counterpartyName!,
      updated.creditLimit ?? undefined,
      updated.netting ?? undefined,
      updated.setoff ?? undefined,
      updated.active ?? false,
    ).then((res) => {
      dispatch(updateCounterpartyRedux({ counterparty: updated }));
    }).catch(() => {
      enqueueSnackbar(`Failed to update counterparty`, { variant: 'error' });
      // Revert only this field back to the original value
      setCounterparties(prev => prev.map(c => c.counterpartyId === counterparty.counterpartyId ? { ...c, [field]: counterparty[field] } : c));
    });
  }

  function saveCounterparty(counterpartyToSave: Partial<CounterpartyModel>) {
    if (!counterpartyToSave.counterpartyId) {
      CommodityManagementApi.addCounterparty(
        saveCounterpartyLoadingState.setLoadingState,
        counterpartyToSave.counterpartyName!,
        counterpartyToSave.creditLimit ?? undefined,
        counterpartyToSave.netting ?? undefined,
        counterpartyToSave.setoff ?? undefined,
      ).then((res) => {
        setCounterparties((previous) => [...previous, res.data.counterparty as CounterpartyModel]);
        dispatch(addCounterpartyRedux({ counterparty: res.data.counterparty! }));
        enqueueSnackbar('Counterparty added successfully', { variant: 'success' });
      })
      .catch(res => {
        enqueueSnackbar('Failed to add counterparty', { variant: 'error' });
      });
    } else {
      CommodityManagementApi.updateCounterparty(
        saveCounterpartyLoadingState.setLoadingState,
        counterpartyToSave.counterpartyId!.toString(),
        counterpartyToSave.counterpartyName!,
        counterpartyToSave.creditLimit ?? undefined,
        counterpartyToSave.netting ?? undefined,
        counterpartyToSave.setoff ?? undefined,
        counterpartyToSave.active ?? false
      ).then((res) => {
        setCounterparties((previous) =>
          previous.map((counterparty) =>
            counterparty.counterpartyId == res.data.counterparty?.counterpartyId ? (res.data.counterparty as CounterpartyModel) : counterparty,
          ),
        );
        dispatch(updateCounterpartyRedux({ counterparty: res.data.counterparty! }));
        enqueueSnackbar('Counterparty updated successfully', { variant: 'success' });
      })
      .catch(res => {
        enqueueSnackbar('Failed to update counterparty', { variant: 'error' });
      });
    }
  }

  function deleteCounterparty(counterpartyToDelete: CounterpartyModel) {
    const counterpartyToDeleteId = counterpartyToDelete.counterpartyId!;
    CommodityManagementApi.deleteCounterparty(null, counterpartyToDeleteId.toString()).then((res) => {
      setCounterparties((previous) => previous.filter((counterparty) => counterparty.counterpartyId != counterpartyToDeleteId));
      dispatch(removeCounterpartyRedux({ counterpartyId: counterpartyToDeleteId }));
    });
  }

  function toggleCounterpartyActive(counterpartyToUpdate: CounterpartyModel) {
    console.log(counterpartyToUpdate);
    saveCounterparty({...counterpartyToUpdate, active: !counterpartyToUpdate.active});
  }

  function addCounterparty() {
    setModalCounterparty(undefined);
    setModalOpen(true);
  }

  function updateCounterparty(counterparty: CounterpartyModel) {
    setModalCounterparty({...counterparty});
    setModalOpen(true);
  }

  return (
    <>
      <StoneXRow align="end">
        <h3>Counterparties</h3>
        <Button className="pull-right" variant="outlined" onClick={addCounterparty}>
          Add
        </Button>
      </StoneXRow>
      <div className="ag-theme-alpine" style={{ width: '100%' }}>
        <StoneXGrid
          hideGridOptions
          ref={gridRef}
          getRowId={(params: GetRowIdParams) => params.data.counterpartyId}
          columns={columnDefs}
          rows={counterparties}
        />
      </div>
      <UpdateCounterpartyModal open={modalOpen} onClose={() => setModalOpen(false)} onCounterpartySave={saveCounterparty} counterparty={modalCounterparty} />
    </>
  );
}

interface UpdateCounterpartyProps {
  open: boolean;
  onClose: () => void;
  onCounterpartySave: (counterparty: Partial<CounterpartyModel>) => void;
  counterparty?: CounterpartyModel;
}

function UpdateCounterpartyModal(props: UpdateCounterpartyProps) {
  const { open, onClose, onCounterpartySave, counterparty } = props;
  const [counterpartyName, setCounterpartyName] = useState<string>();
  const [creditLimit, setCreditLimit] = useState<number | undefined>();
  const [netting, setNetting] = useState<boolean | undefined>();
  const [setoff, setSetoff] = useState<boolean | undefined>();
  const actionLabel = props.counterparty ? 'Update' : 'Add';

  useEffect(() => {
    setCounterpartyName(counterparty?.counterpartyName ?? '');
    setCreditLimit(counterparty?.creditLimit ?? undefined);
    setNetting(counterparty?.netting ?? undefined);
    setSetoff(counterparty?.setoff ?? undefined);
  }, [counterparty]);

  function updateCounterparty() {
    onCounterpartySave({
      counterpartyId: counterparty?.counterpartyId ?? 0,
      counterpartyName: counterpartyName,
      creditLimit: creditLimit,
      netting: netting,
      setoff: setoff,
      active: counterparty?.active
    });

    onClose();
  }

  return (
    <StoneXModal header={actionLabel} open={open} onClose={onClose}>
      <StoneXRow>
        <StoneXTextField label="Counterparty Name" value={counterpartyName} onChange={(event: any) => setCounterpartyName(event.target.value)} />
      </StoneXRow>
      <StoneXRow>
        <StoneXTextField label="Credit Limit" type="number" value={creditLimit} onChange={(event: any) => setCreditLimit(event.target.value)} />
      </StoneXRow>
      <StoneXRow>
        <StoneXCheckbox
          label="Netting"
          checked={netting ?? false}
          onUpdate={x => setNetting(x)} />
      </StoneXRow>
      <StoneXRow>
        <StoneXCheckbox
          label="SetOff"
          checked={setoff ?? false}
          onUpdate={x => setSetoff(x)} />
      </StoneXRow>

      <StoneXRow justify="end">
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={updateCounterparty}>{actionLabel + ' Counterparty'}</Button>
      </StoneXRow>
    </StoneXModal>
  );
}
